import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, Package } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";
import { getBackendUrl } from "../utils/api";

const API_URL = getBackendUrl();

const FleetyGoPaymentStatusPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("verifying"); // verifying, success, failed
  const [orderDetails, setOrderDetails] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    verifyPayment();
  }, [orderId]);

  const verifyPayment = async () => {
    try {
      // Get order number first
      const orderResponse = await axios.get(`${API_URL}/api/fleetygo/orders/${orderId}`);
      const orderNumber = orderResponse.data.order_number;

      // Check payment status from Cashfree
      const statusResponse = await axios.get(`${API_URL}/api/payment/order-status/FLEETYGO_${orderNumber}`);
      
      if (statusResponse.data.order_status === "PAID") {
        // Payment successful - process the order
        const payResponse = await axios.post(`${API_URL}/api/fleetygo/orders/${orderId}/pay`);
        
        // Clear booking data
        sessionStorage.removeItem("fleetygo_booking");
        sessionStorage.removeItem("fleetygo_pending_order");
        
        setOrderDetails({
          ...orderResponse.data,
          rider: payResponse.data.rider,
          pickup_pin: payResponse.data.pickup_pin
        });
        setStatus("success");
      } else if (statusResponse.data.order_status === "ACTIVE") {
        // Payment pending or not completed
        setError("Payment was not completed. Please try again.");
        setStatus("failed");
      } else {
        // Payment failed
        setError("Payment failed. Please try again.");
        setStatus("failed");
      }
    } catch (error) {
      console.error("Payment verification error:", error);
      setError(error.response?.data?.detail || "Failed to verify payment. Please contact support.");
      setStatus("failed");
    }
  };

  if (status === "verifying") {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="bg-white rounded-xl p-8 shadow-sm text-center max-w-sm w-full">
          <Loader2 className="w-16 h-16 text-[#3FA9F5] animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Verifying Payment</h2>
          <p className="text-gray-500">Please wait while we confirm your payment...</p>
        </div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="bg-white rounded-xl p-8 shadow-sm text-center max-w-sm w-full">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Payment Failed</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <div className="space-y-3">
            <Button 
              onClick={() => navigate("/fleetygo/book")}
              className="w-full bg-[#3FA9F5]"
            >
              Try Again
            </Button>
            <Button 
              onClick={() => navigate("/fleetygo")}
              variant="outline"
              className="w-full"
            >
              Go to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="bg-white rounded-xl p-8 shadow-sm text-center max-w-sm w-full">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-10 h-10 text-green-500" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Payment Successful!</h2>
        <p className="text-gray-500 mb-4">Your delivery has been booked</p>
        
        {orderDetails && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <div className="flex items-center gap-2 mb-3">
              <Package className="text-[#3FA9F5]" size={20} />
              <span className="font-medium">Order #{orderDetails.order_number}</span>
            </div>
            
            {orderDetails.rider && (
              <div className="text-sm text-gray-600 mb-2">
                <p><strong>Rider:</strong> {orderDetails.rider.name}</p>
                <p><strong>Vehicle:</strong> {orderDetails.rider.vehicle_number}</p>
              </div>
            )}
            
            {orderDetails.pickup_pin && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
                <p className="text-xs text-yellow-700 mb-1">Pickup PIN</p>
                <p className="text-2xl font-bold tracking-widest text-gray-900">{orderDetails.pickup_pin}</p>
              </div>
            )}
          </div>
        )}
        
        <Button 
          onClick={() => navigate(`/fleetygo/track/${orderId}`)}
          className="w-full bg-[#3FA9F5]"
        >
          Track Your Delivery
        </Button>
      </div>
    </div>
  );
};

export default FleetyGoPaymentStatusPage;
